import React from 'react'
import clsx from 'clsx'
import {
  CircularProgress,
  ClickAwayListener,
  FormControlLabel,
  Popper,
  Radio,
  RadioGroup,
  useTheme
} from '@mui/material'
import { Add, CloseCircle } from 'iconsax-react'

import GreyButton from '../Button/Grey'

import useKeyListener from '@SonarCX/hooks/useKeyListener'
import useStyles from './styles'

const LIMIT = 5

type DropdownProps = {
  options?: any[]
  renderOption?: (val: any) => React.ReactNode
  selected?: any
  onSelect: (val: any) => void
  hideAdd?: boolean
  stringOnly?: boolean
  hideOptions?: boolean
  hideClose?: boolean
  children: React.ReactNode
  duplicates?: boolean
  menuChildren?: (closeFunc: () => void) => React.ReactNode
  disabled?: boolean
  disabledOptions?: any[]
  forceOpen?: boolean
  onClose?: () => void
  disablePortal?: boolean
  colour?: boolean
  closeOnScroll?: boolean
  loading?: boolean
  popperProps?: any
  ChildWrapper?: any
  wrapperClass?: string
}

const Dropdown: React.FC<DropdownProps> = ({
  options = [],
  renderOption = val => val,
  selected,
  onSelect,
  hideAdd,
  stringOnly,
  hideOptions,
  hideClose,
  children,
  duplicates,
  menuChildren = null,
  disabled,
  disabledOptions = [],
  forceOpen,
  onClose,
  disablePortal = false,
  colour = false,
  closeOnScroll = false,
  loading,
  popperProps = {},
  ChildWrapper = React.Fragment,
  wrapperClass
}) => {
  const theme = useTheme()

  const [search, setSearch] = React.useState('')
  const [anchorEl, setAnchorEl] = React.useState(null)
  const open = Boolean(anchorEl)
  const mainRef = React.useRef<HTMLDivElement>()

  const classes = useStyles({ disabled })

  React.useEffect(() => {
    if (forceOpen && mainRef.current) {
      mainRef.current.click()
    }
  }, [forceOpen])

  const handleClick = e => {
    if (!disabled) {
      setAnchorEl(anchorEl ? null : e.currentTarget)
    }
  }

  const handleSearch = e => {
    const val = e.target.value
    setSearch(val)
  }

  const handleClose = React.useCallback(() => {
    setAnchorEl(null)
    onClose && onClose()
  }, [onClose])

  useKeyListener({ key: 'Escape', callback: handleClose })

  const handleSelect = e => {
    onSelect(e.target.value)
    setSearch('')
    handleClose()
  }

  // Auto close on scroll
  React.useEffect(() => {
    const onScroll = () => {
      handleClose()
    }

    if (closeOnScroll) {
      document.addEventListener('wheel', onScroll, { passive: true })
    }

    return () => {
      if (closeOnScroll) {
        document.removeEventListener('wheel', onScroll)
      }
    }
  }, [handleClose, closeOnScroll])

  // Convoluted but, turn options to string, filter on that string, turn string back to option
  let filtered = options
  const hideSearch = filtered.length < LIMIT

  if (!hideSearch && stringOnly) {
    // for searching strings only
    filtered = options
      .map(renderOption)
      .filter(opt => opt?.toLowerCase().includes(search.toLowerCase()))
      .map(val => options.find(opt => val === renderOption(opt)))
  } else if (!hideSearch && duplicates) {
    // for searching lists with duplicate names
    if (filtered.length > 0) {
      filtered = options
        .map(renderOption)
        .filter(option =>
          option.props?.children?.toLowerCase().includes(search.toLowerCase())
        )
        .map(val => options.find(opt => val?.key === opt))
    }
  } else if (!hideSearch) {
    // for searching usernames with avatar
    if (filtered.length > 0) {
      filtered = options
        .map(renderOption)
        .filter(opt =>
          opt?.props.opt?.name?.toLowerCase().includes(search.toLowerCase())
        )
        .map(val => options.find(opt => val?.key === opt))
    }
  }

  // check to hide radio group selects or not
  const identify = () => {
    let userList = false

    if (!hideSearch && !stringOnly && !duplicates && !colour) {
      userList = true
    }

    if (!filtered.length && !hideOptions) {
      return <div>No Options</div>
    } else if (!userList) {
      return (
        <RadioGroup
          name='options'
          value={selected}
          onChange={handleSelect}
          className={classes.radioGroup}
        >
          {filtered.map((opt, i) => {
            const disabled =
              disabledOptions.length > 0 && disabledOptions.includes(opt)

            return (
              <FormControlLabel
                key={`${opt + i}`}
                value={opt}
                label={renderOption ? renderOption(opt) : opt}
                control={
                  <Radio color='primary' size='small' className='radio' />
                }
                className={clsx('label', {
                  [classes.selectedRadio]: selected === opt,
                  [classes.disabledRadio]: disabled
                })}
                disabled={disabled}
              />
            )
          })}
        </RadioGroup>
      )
    } else if (userList) {
      return (
        <RadioGroup
          name='options'
          value={selected}
          onChange={handleSelect}
          className={clsx(classes.radioGroup, classes.radioGroupHide)}
        >
          {filtered.map(opt => (
            <FormControlLabel
              key={opt}
              value={opt}
              label={renderOption ? renderOption(opt) : opt}
              control={<Radio color='primary' size='small' className='radio' />}
              className={clsx(
                'label',
                selected === opt && classes.selectedRadio
              )}
            />
          ))}
        </RadioGroup>
      )
    }
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div className={wrapperClass}>
        <div ref={mainRef} onClick={handleClick} className={classes.children}>
          {open ? children : <ChildWrapper>{children}</ChildWrapper>}
        </div>

        <Popper
          open={open}
          anchorEl={anchorEl}
          style={{ zIndex: '1500' }}
          disablePortal={disablePortal}
          modifiers={[
            { name: 'flip' },
            { name: 'offset', options: { offset: [15, 0] } }
          ]}
          {...popperProps}
        >
          <div className={classes.menu}>
            {hideSearch ? null : (
              <div className={classes.search}>
                {hideSearch ? (
                  <div />
                ) : (
                  <input
                    autoFocus
                    placeholder='Search...'
                    value={search}
                    onChange={handleSearch}
                    className={classes.input}
                  />
                )}

                {hideClose ? (
                  <div />
                ) : (
                  <CloseCircle
                    variant='Bold'
                    size={theme.size.base}
                    onClick={handleClose}
                    className={classes.close}
                  />
                )}
              </div>
            )}

            {loading ? (
              <div className={classes.loading}>
                <CircularProgress />
              </div>
            ) : (
              identify()
            )}

            {menuChildren && menuChildren(handleClose)}

            {!hideAdd && (
              <GreyButton>
                <Add size={theme.size.base} className={classes.buttonIcon} />{' '}
                ADD
              </GreyButton>
            )}
          </div>
        </Popper>
      </div>
    </ClickAwayListener>
  )
}

export default Dropdown
